/**
 * 全站共享的领域常量。任何"魔法字符串"都应来自这里，前后端共用。
 */

/** 家庭内角色（家长 / 学生）。将来产品化时新增 teacher（见方案 7 章阶段 5）。 */
export const ROLES = ['parent', 'student'] as const;
export type Role = (typeof ROLES)[number];

/**
 * 全部账号角色，含**系统管理员**（见方案 2B）。
 *
 * 为什么单独再列一份而不是把 system_admin 塞进 ROLES：
 * ROLES 的语义是"家庭内角色"，注册、学生创建、共享等用例都建立在它之上。
 * 把管理员混进去，那些用例会在类型层面悄悄放宽（例如允许注册出 system_admin）。
 * 因此：
 *   - ROLES        → 家庭内角色（家长/学生）
 *   - ACCOUNT_ROLES→ 账号表 `auth_user.role` 的全部取值（含 system_admin）
 */
export const ACCOUNT_ROLES = ['parent', 'student', 'system_admin'] as const;
export type AccountRole = (typeof ACCOUNT_ROLES)[number];

/** 系统管理员角色标识（与 DB 的 role 枚举取值一致，见方案 2B）。 */
export const SYSTEM_ADMIN_ROLE = 'system_admin' as const;

/**
 * 邮箱唯一性所覆盖的角色 —— 即"家庭内账号"。
 *
 * 为什么需要这个常量：管理员与家长是**两套独立账号体系**（方案 2B.1），
 * 所以同一个邮箱允许"既是某个家长、又是管理员"。
 * 代价是邮箱不再全局唯一，于是**所有按邮箱查账号的地方都必须带上这个角色条件**，
 * 否则可能误命中管理员账号（例如家长找回密码时把管理员的账号翻出来）。
 *
 * 把条件收敛到这一个常量，比在每个查询里手写 `role='parent'` 更不容易漏。
 * 数据库侧的对应约束是 `uq_auth_user_parent_email`（WHERE role='parent'）。
 */
export const FAMILY_EMAIL_ROLES = ['parent'] as const;

/** 家长身份：仅预置爸爸/妈妈（已与用户确认），但库里允许自定义标签。 */
export const PARENT_RELATION_PRESETS = ['爸爸', '妈妈'] as const;

export const GENDERS = ['male', 'female'] as const;
export type Gender = (typeof GENDERS)[number];

/**
 * 年级编码。用稳定 code 存库，展示名可本地化。
 * uiMode 决定学生端界面：child=大按钮图形化儿童模式。
 */
export const GRADES = [
  { code: 'primary_1', label: '小学一年级', stage: 'primary', uiMode: 'child' },
  { code: 'primary_2', label: '小学二年级', stage: 'primary', uiMode: 'child' },
  { code: 'primary_3', label: '小学三年级', stage: 'primary', uiMode: 'child' },
  { code: 'primary_4', label: '小学四年级', stage: 'primary', uiMode: 'bridge' },
  { code: 'primary_5', label: '小学五年级', stage: 'primary', uiMode: 'bridge' },
  { code: 'primary_6', label: '小学六年级', stage: 'primary', uiMode: 'bridge' },
  { code: 'junior_1', label: '初中一年级', stage: 'junior', uiMode: 'full' },
  { code: 'junior_2', label: '初中二年级', stage: 'junior', uiMode: 'full' },
  { code: 'junior_3', label: '初中三年级', stage: 'junior', uiMode: 'full' },
  { code: 'senior_1', label: '高中一年级', stage: 'senior', uiMode: 'full' },
  { code: 'senior_2', label: '高中二年级', stage: 'senior', uiMode: 'full' },
  { code: 'senior_3', label: '高中三年级', stage: 'senior', uiMode: 'full' },
] as const;

export type GradeCode = (typeof GRADES)[number]['code'];
export type UiMode = (typeof GRADES)[number]['uiMode'];

export const GRADE_CODES = GRADES.map((g) => g.code) as GradeCode[];

export function gradeToUiMode(code: string): UiMode {
  return GRADES.find((g) => g.code === code)?.uiMode ?? 'full';
}

export function gradeLabel(code: string): string {
  return GRADES.find((g) => g.code === code)?.label ?? code;
}

/**
 * 节点 kind 的**全部取值**（与数据库的 `node_kind` 枚举一致）。
 *
 * ⚠️ 这个列表包含 `homework`，因为**响应里可能出现它**（类型必须能表达数据库的真实取值）。
 * 但"用户能通过通用 `/nodes` 接口创建哪些 kind"是另一件事 —— 见 `USER_NODE_KINDS`。
 */
export const NODE_KINDS = ['goal', 'task', 'homework'] as const;
export type NodeKind = (typeof NODE_KINDS)[number];

/**
 * **通用 `/nodes` 接口允许创建 / 查询的 kind**（作业节点只能由作业模块写入）。
 *
 * 为什么必须与 `NODE_KINDS` 分开：
 *   `kind='homework'` 的节点是"某一天的作业单 / 某个科目"，它们由
 *   `apps/api/src/modules/homework/` 按固定结构维护，并且**刻意不出现在目标页**。
 *   如果通用节点接口也接受它，任何人都能造出一个"孤儿作业节点"——
 *   它既不在目标树里（被排除），结构上又不满足作业单的约定，
 *   只会变成谁都看不见、谁也管不了的脏数据。
 *
 * 因此：`POST /students/:id/nodes` 与 `GET .../nodes?kind=` 都只认这两个值。
 */
export const USER_NODE_KINDS = ['goal', 'task'] as const;
export type UserNodeKind = (typeof USER_NODE_KINDS)[number];

/** 作业节点的 kind（`node_kind` 枚举里的第三个值，只由作业模块写入）。 */
export const HOMEWORK_NODE_KIND = 'homework' as const;

/**
 * 作业科目的**默认截止时刻**（`node.due_time`）。
 *
 * 语义是"当天的作业当天完成"：家长没填截止时刻、也没填"开始 + 时长"时，
 * 服务端把科目的截止时刻落成 23:59，并在响应里标 `dueTimeSource: 'dayEnd'`。
 *
 * 为什么不留空（NULL）：`checkin.due_at` 的唯一来源就是 `node.due_time`
 * （API 与 worker 两侧已统一），留空会退化成"当天 0 点"，
 * 于是每一条当天布置的作业从 00:00 起就被判成「超时」—— 假超时。
 *
 * 为什么是 23:59 而不是 24:00：`time` 类型接受 `24:00:00`，但 shared 的
 * `localDateTimeUtc()` 只接受 `HH:mm[:ss]` 且小时 ≤ 23，写 24:00 会让换算抛错。
 */
export const HOMEWORK_DEFAULT_DUE_TIME = '23:59';

/**
 * 作业项的**默认分值**（`habit.points_per_unit`），单位是"每完成一个单位得几分"。
 *
 * ⚠️ 这里是 **0**，依据是**用户自己明确的决定**：
 *   「作业目前我是不计分的」—— 作业是每天要完成的义务，不该再额外给分
 *   （与"打卡任务"不同：那些是家长为了培养习惯而设的目标，计分才有意义）。
 *
 * 历史（值得记下来，避免以后有人"顺手改成 1"）：
 *   这个常量一度是 `1`，理由是"与新建打卡任务表单的默认值一致"，而且当时认为
 *   "录作业时没填分值 = 按默认规则计分"。但那是**助手的推断，不是用户的要求** ——
 *   用户随后明确说作业不计分。于是：**默认必须是 0**，
 *   否则家长新建的每一条作业项都会悄悄带上 1 分，作业就计分了。
 *
 * 语义边界（0 与"没设置"是两件事，见 `homework-draft.ts` 的 `resolveHomeworkItemPoints`）：
 *   - `0` = **不计分**（要么是默认，要么是家长明确选的）；
 *   - **没设置**（请求里根本没这个字段）→ 落到这里：0 分。
 *   - 家长若**显式**填了分值（例如 2），就按填的算 —— 想给某项计分仍然可行。
 */
export const HOMEWORK_DEFAULT_POINTS_PER_UNIT = 0;

/**
 * 打卡任务积分规则的上下限（见方案 3.4.1，迁移 0011）。
 *
 * 它们是**同一个契约**的三处实现里共用的数字：zod（`createHabitSchema` /
 * `updateHabitSchema`）、家长端表单的 `validatePointsInput`、以及数据库
 * `ck_habit_points_unit_size`（`>= 1` 那一半）。
 * 抽成常量就是为了让"下限/上限改了但某一处没改"变成一个编译期就能发现的引用，
 * 而不是三份各自写着 `1000` / `10000` / `1` 的字面量。
 */
/** N 的下限：`0` 会让算分公式除零，所以是 1（DB CHECK 也钉住这一条）。 */
export const MIN_POINTS_UNIT_SIZE = 1;
/** 「每 N 个单位」里的 N 上限。N 越大分值越小，超过这个数已无实际意义。 */
export const MAX_POINTS_UNIT_SIZE = 10000;
/** 「得 M 分」里的 M 上限（沿用改造前的既有上限，不改动存量语义）。 */
export const MAX_POINTS_PER_UNIT = 1000;

/** 最多三级（见方案 1.3 ⑫，数据库触发器强制）。 */
export const MAX_NODE_DEPTH = 3;

/**
 * 节点状态（目标与任务共用）。
 *
 * `archived` **不再是一个状态** —— 归档是独立维度（`node.archived_at`），
 * 因为"已归档的已完成目标"用状态枚举根本表达不了。
 */
export const NODE_STATUSES = ['active', 'paused', 'completed', 'stopped'] as const;
export type NodeStatus = (typeof NODE_STATUSES)[number];

/**
 * 状态中文标签。**终态可逆**：完成/停止可以"重新开启"回到进行中 ——
 * 绝对不可逆在手机上一次误触就锁死，实用中一定出问题。
 */
export const NODE_STATUS_LABEL: Record<NodeStatus, string> = {
  active: '进行中',
  paused: '暂停',
  completed: '完成',
  stopped: '停止',
};

/** 终态：不再向前推进（但仍可"重新开启"）。 */
export const TERMINAL_NODE_STATUSES: readonly NodeStatus[] = ['completed', 'stopped'];

/** 某状态是否终态。 */
export function isTerminalNodeStatus(status: NodeStatus): boolean {
  return status === 'completed' || status === 'stopped';
}

export const CREATOR_TYPES = ['parent', 'student'] as const;
export type CreatorType = (typeof CREATOR_TYPES)[number];

/** 共享权限三级（见方案 1.2 ⑧）。 */
export const SHARE_PERMISSIONS = ['owner', 'editor', 'viewer'] as const;
export type SharePermission = (typeof SHARE_PERMISSIONS)[number];

export const SHARE_STATUSES = [
  'pending',
  'accepted',
  'rejected',
  'revoked',
  'left',
  'expired',
] as const;
export type ShareStatus = (typeof SHARE_STATUSES)[number];

/** 打卡任务频率。`once` = 一次性（作业明细、一次性事项）。 */
export const RECURRENCES = ['daily', 'weekly', 'monthly', 'custom', 'once'] as const;
export type Recurrence = (typeof RECURRENCES)[number];

/** 频率的中文标签（界面共用，避免多处各写一套说法）。 */
export const RECURRENCE_LABEL: Record<Recurrence, string> = {
  daily: '每天',
  weekly: '每周（选周几）',
  monthly: '每月（选日期）',
  custom: '自定义（每 N 天）',
  once: '一次性（只在生效开始那天）',
};

/** 打卡记录状态机（见方案 2.3）。 */
export const CHECKIN_STATUSES = [
  'pending',
  'running',
  'paused',
  'auto_paused',
  'done',
  /** 已提交、待家长验收（`habit.need_approval = true` 时出现；此时尚未计分）。 */
  'submitted',
  'missed',
  'skipped',
] as const;
export type CheckinStatus = (typeof CHECKIN_STATUSES)[number];

export const CHECKIN_EVENTS = [
  'start',
  'pause',
  'resume',
  'finish',
  'auto_pause',
  'skip',
  'undo',
  'makeup',
  /** 家长验收通过（submitted → done，此时才计分）。 */
  'approve',
  /** 家长打回重做（submitted → pending，不计分）。 */
  'reject',
] as const;
export type CheckinEventType = (typeof CHECKIN_EVENTS)[number];

/** 积分流水原因（见方案 3.4）。 */
export const POINTS_REASONS = [
  'checkin',
  'makeup',
  'bonus',
  'penalty',
  'redeem',
  'redeem_refund',
  'adjust',
] as const;
export type PointsReason = (typeof POINTS_REASONS)[number];

export const REDEMPTION_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'fulfilled',
  'cancelled',
] as const;
export type RedemptionStatus = (typeof REDEMPTION_STATUSES)[number];

export const COMMENT_TARGETS = ['checkin', 'node', 'redemption', 'period'] as const;
export type CommentTarget = (typeof COMMENT_TARGETS)[number];

/** 用户可覆盖学生端的界面模式（见方案 2.5）。 */
export const UI_MODES = ['child', 'bridge', 'full'] as const;

/** 默认家庭时区（已确认 Asia/Shanghai）。 */
export const DEFAULT_FAMILY_TIMEZONE = 'Asia/Shanghai';

/** 计时相关阈值。 */
export const HEARTBEAT_INTERVAL_SECONDS = 30;
/** 运行中超过「预计时长 × 该倍数」且无心跳 → 自动暂停（见方案 2.3）。 */
export const AUTO_PAUSE_MULTIPLIER = 2;
/** 补卡宽限期（小时）：次日该时长内允许把 missed 改为 done。 */
export const DEFAULT_GRACE_HOURS = 24;
